// web/src/crypto/shards.ts — envelope + Shamir + per-recipient shard crypto (M1a.1/.2/.4).
//
// Authoritative contract: shared/crypto-contract.md (ADR-001, D-014). This module is the
// confidentiality core of the client: it seals a secret under a fresh random data key,
// Shamir-splits ONLY the data key, and encrypts each share to one recipient's public key.
// The inverse path decrypts a holder's own shard and combines K shares back to the data
// key, then opens the envelope.
//
// Hard invariant (R11): only `Envelope`, `WireShard` and `PublicKeyJwk` ever cross a
// function boundary that can lead to the network — all opaque ciphertext or public
// material. `DataKey`, `RawShare` and the plaintext stay in-browser only.
//
// Algorithms (ADR-001 §Decision):
//   - Envelope:        AES-256-GCM, 96-bit random IV, tag appended by WebCrypto.
//   - Shamir:          secrets.js-grempe (GF(2^8), self-indexing hex shares) over the
//                      32-byte data key, threshold K of N. Integrity is NOT on the share;
//                      a wrong/forged share set yields a wrong data key that fails the
//                      envelope GCM tag deterministically.
//   - Shard encryption: ephemeral ECDH P-256 -> HKDF-SHA-256 -> AES-256-GCM. The ephemeral
//                      public key rides in the WireShard; the holder re-derives with their
//                      non-extractable private key. Per-shard forward secrecy.

import * as sss from 'secrets.js-grempe';

import { CRYPTO_FORMAT_VERSION, CryptoError } from './types';
import type {
  Base64Url,
  DataKey,
  Envelope,
  EnvelopeCodec,
  PrivateKeyHandle,
  PublicKeyId,
  PublicKeyJwk,
  RawShare,
  ShamirCodec,
  ShardEncryptor,
  WireShard,
} from './types';

// ---------------------------------------------------------------------------
// secrets.js-grempe initialisation
// ---------------------------------------------------------------------------
//
// Force the browser CSPRNG and an 8-bit field. The library's feature detection picks
// browserCryptoGetRandomValues in a real browser; we set it explicitly so behaviour is
// deterministic across the SPA and test runners that expose WebCrypto. 8-bit config is
// the library default and is what its self-indexing share format assumes.

let sssReady = false;
function ensureSss(): void {
  if (sssReady) return;
  // 8-bit field => up to 255 shares, matching the contract's `2 <= k <= n <= 255`.
  // Prefer the browser CSPRNG; the library auto-detects node crypto in a test VM.
  try {
    sss.init(8, 'browserCryptoGetRandomValues');
  } catch {
    // Fall back to feature-detected CSPRNG. secrets.js-grempe refuses a non-crypto RNG.
    // The rngType arg is optional at runtime; the bundled typings mark it required.
    (sss.init as (bits: number) => void)(8);
  }
  sssReady = true;
}

// ---------------------------------------------------------------------------
// Encoding helpers (exported — keys.ts and tests consume these)
// ---------------------------------------------------------------------------

/** Encode bytes as base64url (RFC 4648 §5, no padding). */
export function bytesToBase64Url(bytes: Uint8Array): Base64Url {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  const b64 = btoa(bin);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '') as Base64Url;
}

/** Decode a base64url string (padding optional) back to bytes. */
export function base64UrlToBytes(s: Base64Url | string): Uint8Array {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
  const bin = atob(b64 + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

function hexToBytes(hex: string): Uint8Array {
  const clean = hex.length % 2 === 0 ? hex : `0${hex}`;
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}

// ---------------------------------------------------------------------------
// JWK -> SPKI-DER (exported — keys.ts derives the keyId/fingerprint from this)
// ---------------------------------------------------------------------------
//
// We hand-encode the SubjectPublicKeyInfo DER for an uncompressed P-256 point so the
// fingerprint hash is stable and independent of WebCrypto export availability for a JWK
// that may not be backed by a live CryptoKey. The prefix is the fixed DER header for
// id-ecPublicKey + prime256v1 followed by the BIT STRING wrapper of the 0x04||X||Y point.

// 26-byte ASN.1 prefix: SEQUENCE { SEQUENCE { OID ecPublicKey, OID prime256v1 },
// BIT STRING (unused bits = 0) of length 66 (0x04 + 32 + 32). }
const SPKI_P256_PREFIX = new Uint8Array([
  0x30, 0x59, // SEQUENCE, len 89
  0x30, 0x13, // SEQUENCE, len 19
  0x06, 0x07, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x02, 0x01, // OID 1.2.840.10045.2.1 (ecPublicKey)
  0x06, 0x08, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x03, 0x01, 0x07, // OID 1.2.840.10045.3.1.7 (prime256v1)
  0x03, 0x42, 0x00, // BIT STRING, len 66, 0 unused bits
]);

/** Build the SPKI-DER bytes for a P-256 public JWK (uncompressed point form). */
export function jwkToSpkiDer(publicKey: PublicKeyJwk): Uint8Array {
  const x = base64UrlToBytes(publicKey.x);
  const y = base64UrlToBytes(publicKey.y);
  if (x.length !== 32 || y.length !== 32) {
    throw new CryptoError('keygen_failed', 'P-256 JWK coordinates must be 32 bytes each');
  }
  const der = new Uint8Array(SPKI_P256_PREFIX.length + 1 + 64);
  der.set(SPKI_P256_PREFIX, 0);
  der[SPKI_P256_PREFIX.length] = 0x04; // uncompressed point indicator
  der.set(x, SPKI_P256_PREFIX.length + 1);
  der.set(y, SPKI_P256_PREFIX.length + 1 + 32);
  return der;
}

// ---------------------------------------------------------------------------
// Envelope codec — AES-256-GCM seal/open of the secret under the data key
// ---------------------------------------------------------------------------

const GCM_IV_BYTES = 12; // 96-bit IV, the GCM-recommended size
const DATA_KEY_BYTES = 32; // 256-bit

async function importGcmKey(raw: Uint8Array, usage: KeyUsage): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw as unknown as ArrayBuffer, { name: 'AES-GCM' }, false, [
    usage,
  ]);
}

class WebCryptoEnvelopeCodec implements EnvelopeCodec {
  generateDataKey(): DataKey {
    const k = new Uint8Array(DATA_KEY_BYTES);
    crypto.getRandomValues(k);
    return k as DataKey;
  }

  async seal(plaintext: Uint8Array, dataKey: DataKey): Promise<Envelope> {
    const iv = new Uint8Array(GCM_IV_BYTES);
    crypto.getRandomValues(iv);
    const key = await importGcmKey(dataKey, 'encrypt');
    const ct = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv as unknown as ArrayBuffer },
      key,
      plaintext as unknown as ArrayBuffer,
    );
    return {
      v: CRYPTO_FORMAT_VERSION,
      alg: 'AES-256-GCM',
      iv: bytesToBase64Url(iv),
      ciphertext: bytesToBase64Url(new Uint8Array(ct)),
    };
  }

  async open(envelope: Envelope, dataKey: DataKey): Promise<Uint8Array> {
    const iv = base64UrlToBytes(envelope.iv);
    const ct = base64UrlToBytes(envelope.ciphertext);
    const key = await importGcmKey(dataKey, 'decrypt');
    try {
      const pt = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv as unknown as ArrayBuffer },
        key,
        ct as unknown as ArrayBuffer,
      );
      return new Uint8Array(pt);
    } catch {
      // A wrong recovered data key (K-1 / forged shares) lands here too: the GCM tag
      // fails to verify, which is exactly the integrity signal the ADR relies on.
      throw new CryptoError('envelope_tampered', 'envelope GCM tag verification failed');
    }
  }
}

// ---------------------------------------------------------------------------
// Shamir codec — split/combine the 32-byte data key (secrets.js-grempe)
// ---------------------------------------------------------------------------

class GrempeShamirCodec implements ShamirCodec {
  split(dataKey: DataKey, n: number, k: number): RawShare[] {
    if (!(Number.isInteger(n) && Number.isInteger(k)) || k < 2 || n < k || n > 255) {
      throw new CryptoError('combine_failed', `invalid (n=${n}, k=${k}); require 2 <= k <= n <= 255`);
    }
    ensureSss();
    const secretHex = bytesToHex(dataKey);
    return sss.share(secretHex, n, k) as RawShare[];
  }

  combine(shares: RawShare[]): DataKey {
    if (!Array.isArray(shares) || shares.length === 0) {
      throw new CryptoError('combine_failed', 'no shares supplied');
    }
    ensureSss();
    let recoveredHex: string;
    try {
      recoveredHex = sss.combine(shares);
    } catch (e) {
      throw new CryptoError('combine_failed', String((e as Error)?.message ?? e));
    }
    const bytes = hexToBytes(recoveredHex);
    // With < k correct shares secrets.js returns a (wrong) value, often the wrong length;
    // normalise to 32 bytes so the envelope GCM tag — not a length check — is the gate.
    if (bytes.length !== DATA_KEY_BYTES) {
      const fixed = new Uint8Array(DATA_KEY_BYTES);
      fixed.set(bytes.slice(0, DATA_KEY_BYTES));
      return fixed as DataKey;
    }
    return bytes as DataKey;
  }
}

// ---------------------------------------------------------------------------
// Shard encryptor — ECDH P-256 -> HKDF-SHA-256 -> AES-256-GCM, per recipient
// ---------------------------------------------------------------------------

const SHARD_ALG = 'ECDH-P256+HKDF-SHA256+AES-256-GCM' as const;
const HKDF_INFO = new TextEncoder().encode('breakglass/shard/v1'); // context binding (ADR OQ1)
const HKDF_SALT = new Uint8Array(0); // empty salt; context is carried in `info`

/** Import a public JWK as an ECDH P-256 public CryptoKey. */
function importEcdhPublic(jwk: PublicKeyJwk): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'jwk',
    { kty: jwk.kty, crv: jwk.crv, x: jwk.x, y: jwk.y, ext: true },
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    [],
  );
}

/** Export an ephemeral public CryptoKey to the wire JWK shape. */
async function exportEphemeralJwk(key: CryptoKey): Promise<PublicKeyJwk> {
  const jwk = await crypto.subtle.exportKey('jwk', key);
  if (jwk.kty !== 'EC' || jwk.crv !== 'P-256' || !jwk.x || !jwk.y) {
    throw new CryptoError('keygen_failed', 'ephemeral public key is not P-256 EC');
  }
  return { kty: 'EC', crv: 'P-256', x: jwk.x, y: jwk.y, ext: true, key_ops: [] };
}

/** ECDH(local private, peer public) -> HKDF-SHA-256 -> 256-bit AES-GCM key. */
async function deriveSharedAesKey(
  privateKey: CryptoKey,
  peerPublic: CryptoKey,
  usage: KeyUsage,
): Promise<CryptoKey> {
  const sharedBits = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: peerPublic },
    privateKey,
    256,
  );
  const hkdfKey = await crypto.subtle.importKey('raw', sharedBits, { name: 'HKDF' }, false, [
    'deriveKey',
  ]);
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: HKDF_SALT as unknown as ArrayBuffer,
      info: HKDF_INFO as unknown as ArrayBuffer,
    },
    hkdfKey,
    { name: 'AES-GCM', length: 256 },
    false,
    [usage],
  );
}

class WebCryptoShardEncryptor implements ShardEncryptor {
  async encryptShare(
    share: RawShare,
    shareIndex: number,
    recipient: { keyId: PublicKeyId; publicKey: PublicKeyJwk },
  ): Promise<WireShard> {
    const peerPublic = await importEcdhPublic(recipient.publicKey);
    const ephemeral = (await crypto.subtle.generateKey(
      { name: 'ECDH', namedCurve: 'P-256' },
      true, // ephemeral public must be exportable to ride in the WireShard
      ['deriveBits'],
    )) as CryptoKeyPair;

    const aesKey = await deriveSharedAesKey(ephemeral.privateKey, peerPublic, 'encrypt');
    const iv = new Uint8Array(GCM_IV_BYTES);
    crypto.getRandomValues(iv);
    const ct = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv as unknown as ArrayBuffer },
      aesKey,
      new TextEncoder().encode(share) as unknown as ArrayBuffer,
    );

    return {
      v: CRYPTO_FORMAT_VERSION,
      alg: SHARD_ALG,
      recipientKeyId: recipient.keyId,
      ephemeralPubKey: await exportEphemeralJwk(ephemeral.publicKey),
      iv: bytesToBase64Url(iv),
      ciphertext: bytesToBase64Url(new Uint8Array(ct)),
      shareIndex,
    };
  }

  async decryptShare(shard: WireShard, privateKey: PrivateKeyHandle): Promise<RawShare> {
    if (shard.alg !== SHARD_ALG) {
      // Versioning is immutable: refuse unknown suites rather than guessing (ADR §wire).
      throw new CryptoError('shard_tampered', `unsupported shard suite ${shard.alg}`);
    }
    if (shard.recipientKeyId !== privateKey.keyId) {
      throw new CryptoError('wrong_recipient', 'shard is sealed to a different keyId');
    }
    const ephemeralPublic = await importEcdhPublic(shard.ephemeralPubKey);
    const aesKey = await deriveSharedAesKey(privateKey.cryptoKey, ephemeralPublic, 'decrypt');
    const iv = base64UrlToBytes(shard.iv);
    const ct = base64UrlToBytes(shard.ciphertext);
    try {
      const pt = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv as unknown as ArrayBuffer },
        aesKey,
        ct as unknown as ArrayBuffer,
      );
      return new TextDecoder().decode(new Uint8Array(pt)) as RawShare;
    } catch {
      throw new CryptoError('shard_tampered', 'shard GCM tag verification failed');
    }
  }
}

// ---------------------------------------------------------------------------
// Codec singletons (the module's public surface; re-exported via crypto/index.ts)
// ---------------------------------------------------------------------------

export const envelopeCodec: EnvelopeCodec = new WebCryptoEnvelopeCodec();
export const shamirCodec: ShamirCodec = new GrempeShamirCodec();
export const shardEncryptor: ShardEncryptor = new WebCryptoShardEncryptor();

// ---------------------------------------------------------------------------
// High-level seal / unseal flow (ADR-001 §"End-to-end shape")
// ---------------------------------------------------------------------------

/** Output of {@link sealSecret}: the opaque envelope plus one encrypted shard per recipient. */
export interface SealResult {
  envelope: Envelope;
  encryptedShares: WireShard[];
}

/**
 * Seal a secret for an M-of-N quorum:
 *   1. generate a random data key,
 *   2. AES-256-GCM encrypt the secret under it (the Envelope),
 *   3. Shamir-split ONLY the data key into N shares, threshold K,
 *   4. ECDH-encrypt each share to its recipient's public key.
 * Returns the opaque envelope + one WireShard per recipient. The data key and raw shares
 * never leave this function; only `envelope` + `encryptedShares` are network-safe.
 */
export async function sealSecret(
  plaintext: Uint8Array,
  recipients: Array<{ keyId: PublicKeyId; publicKey: PublicKeyJwk }>,
  threshold: number,
): Promise<SealResult> {
  const n = recipients.length;
  if (n < 2 || threshold < 2 || threshold > n) {
    throw new CryptoError('combine_failed', `invalid quorum (k=${threshold}, n=${n})`);
  }

  const dataKey = envelopeCodec.generateDataKey();
  const envelope = await envelopeCodec.seal(plaintext, dataKey);
  const shares = shamirCodec.split(dataKey, n, threshold);

  const encryptedShares = await Promise.all(
    recipients.map((recipient, i) =>
      // secrets.js shares self-index; shareIndex here is the 1-based ceremony slot.
      shardEncryptor.encryptShare(shares[i], i + 1, recipient),
    ),
  );

  // Best-effort scrub of the data key bytes from this frame.
  (dataKey as Uint8Array).fill(0);

  return { envelope, encryptedShares };
}

/** Decrypt one held shard back to its raw Shamir share (holder's private key required). */
export function decryptShare(shard: WireShard, privateKey: PrivateKeyHandle): Promise<RawShare> {
  return shardEncryptor.decryptShare(shard, privateKey);
}

/**
 * Combine K decrypted shares back into the data key and open the envelope.
 * Fewer than K, or K-1 valid + noise, recovers a WRONG data key whose GCM tag fails —
 * surfaced as CryptoError('envelope_tampered'). Returns the plaintext, in-browser only.
 */
export async function combineAndOpen(
  shares: RawShare[],
  envelope: Envelope,
): Promise<Uint8Array> {
  const dataKey = shamirCodec.combine(shares);
  try {
    return await envelopeCodec.open(envelope, dataKey);
  } finally {
    (dataKey as Uint8Array).fill(0);
  }
}
